import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { BannerShapes } from '../common/icons';
import { Button } from '../common/ui/Button';
import { Input } from '../common/ui/Input';

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { fetchUserWithRetry } from '../../utils/profileUtils';
import { captureCustomEvent } from '../../utils/posthogUtils';
import { VerificationCodeInput } from './VerificationCodeInput';
import { OAuthButtonGroup } from './common/OAuthButtonGroup';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string>()
  const turnstileRef = useRef<TurnstileInstance>(null);


  useEffect(() => {
    captureCustomEvent('login_started', { eventProperties: { method: 'page_load' } });
    
    // Disabled invite flows: clear stale invite payloads to prevent redirects.
    const judgeInviteData = localStorage.getItem('judgeInvite');
    const workspaceInviteData = localStorage.getItem('workspaceInvite');
    
    if (judgeInviteData) {
      localStorage.removeItem('judgeInvite');
    }

    if (workspaceInviteData) {
      localStorage.removeItem('workspaceInvite');
    }

    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => {
    // TODO: Extract OAuth callback handling into a shared component/hook
    // Currently duplicated between LoginPage and SignupPage - should be refactored
    // into a reusable OAuth handler to avoid maintaining identical logic in multiple places
    // TODO: SignupPage still uses handleLinkedInCallback name - should be renamed to handleOAuthCallback for consistency
    const handleOAuthCallback = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        captureAppException(error, {
          tags: {
            context: 'auth_login',
            action: 'oauth_callback'
          }
        });
        console.error('Error checking auth session:', error);
        return;
      }

      if (session) {
        if (!session.user) {
          captureAppException(new Error('OAuth session missing user data'), {
            tags: {
              context: 'auth_login',
              action: 'oauth_callback',
              issue: 'session_no_user'
            }
          });
          return;
        }
        const profile = await fetchUserWithRetry(session.user.id, 'oauth_profile_fetch');

        if (!profile) {
          // fetchUserWithRetry already captured the error, just return
          return;
        }

        if (profile.profile_completed) {
          captureCustomEvent('login_successful', { eventProperties: { method: 'oauth' } });
          navigate('/'); // Navigate to dashboard on success
        } else {
          // Store returnUrl if it exists
          const urlParams = new URLSearchParams(window.location.search);
          const returnUrl = urlParams.get('returnUrl');
          if (returnUrl) {
            localStorage.setItem('returnUrl', returnUrl);
          }
          
          // Navigate to onboarding for OAuth users with incomplete profiles
          navigate('/register/onboarding');
        }
      } else {
        // No session found after OAuth callback - this indicates OAuth failure
        captureAppException(new Error('No session found after OAuth callback'), {
          tags: {
            context: 'auth_login',
            action: 'oauth_callback',
            issue: 'no_session',
            url_hash: window.location.hash
          }
        });
      }
    };

    if (window.location.hash.includes('access_token')) {
      handleOAuthCallback();
    }
  }, [navigate]);

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(120); // 2 minutes
  };

  const handleResendVerification = async () => {
    if (resendDisabled) return;

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { captchaToken },
      });

      if (error) throw error;
      startResendTimer();
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'auth_login',
          email: email,
          action: 'resend_verification'
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLoginVerification = async () => {
    try {
      // After OTP verification, try to sign in again with the original password
      const { error: signInError, data } = await supabase.auth.signInWithPassword({
        email,
        password, // Assumes password is still in state from initial login attempt
        options: { captchaToken },
      });

      if (signInError) throw signInError;

      if (data.session) {
        captureCustomEvent('login_successful', { eventProperties: { method: 'email_otp_verified' } });
        navigate('/'); // Navigate to dashboard on success
      } else {
        const error = new Error('Sign-in after verification failed: No session data.');
        captureAppException(error, {
          tags: {
            context: 'auth_login',
            action: 'signin_after_otp_verification',
            issue: 'no_session_after_signin',
            email: email
          }
        });
        throw error;
      }
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'auth_login',
          email: email,
          action: 'signin_after_otp_verification'
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to login after verification.');
    } finally {
      setLoading(false); // Ensure loading is set to false in all cases
    }
  };

  const handleFailedLoginVerification = (errorMessage: string) => {
    setError(errorMessage);
    captureAppException(new Error(errorMessage), { // Capture the OTP verification failure
      tags: {
        context: 'auth_login_otp_verify',
        email: email,
        action: 'otp_verification_failed' // More specific action
      }
    });
    setLoading(false);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Ensure we have a captcha token before proceeding
    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification.');
      setLoading(false);
      return;
    }

    try {
      const { error: signInError, data } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (signInError?.message === 'Email not confirmed') {
        // Send verification email and show verification UI
        const { error: resendError } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { captchaToken },
        });

        // Only reset Turnstile widget, don't clear token state here
        turnstileRef.current?.reset(); 

        if (resendError) throw resendError;

        setShowVerification(true);
        startResendTimer();
        return; // Exit here, don't proceed to navigate
      }

      if (signInError) throw signInError;

      // Successful login
      if (data.session) {
        captureCustomEvent('login_successful', { eventProperties: { method: 'email' } });
        navigate('/'); // Navigate to dashboard on success
      }
    } catch (err) {
      // Special handling for invalid login credentials
      if (err instanceof Error && err.message && (
        err.message.toLowerCase().includes('invalid login credentials') ||
        err.message.toLowerCase().includes('invalid email or password')
      )) {
        captureCustomEvent('login_failed_invalid_credentials', {
          eventProperties: {
            context: 'auth_login',
            email: email,
            action: 'sign_in',
            error_message: err.message
          }
        });
        setError('Invalid email or password. Please try again.');
      } else if (err instanceof Error && err.message && 
        err.message.toLowerCase().includes('timeout-or-duplicate')) {
        captureCustomEvent('login_failed_captcha_timeout', {
          eventProperties: {
            context: 'auth_login',
            email: email,
            action: 'sign_in',
            error_message: err.message
          }
        });
        setError('reCAPTCHA verification failed. Please reload the page and try again.');
      } else {
        captureAppException(err, {
          tags: {
            context: 'auth_login',
            action: 'sign_in',
            email: email
          }
        });
        setError('An error occurred during login. Please try again.');
      }
      // Only reset Turnstile widget, don't clear token state here
      turnstileRef.current?.reset(); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1628] flex flex-col">
      <div className="absolute inset-0 pointer-events-none">
        <BannerShapes className="w-full h-full px-10" />
      </div>
      {/* Header */}
      <header className="z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img
                src="/holito-logo-white.svg"
                alt="holito"
                className="h-8"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 z-10">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Login to holito communities</h1>
            <p className="text-gray-600 mt-2">Login to an existing account</p>
          </div>

          {error && (
            <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          <Turnstile
            ref={turnstileRef}
            siteKey="0x4AAAAAAA5Qj30yypsP-5e5"
            onSuccess={(token: string) => {
              setCaptchaToken(token)
              setError(null);
            }}
            onError={() => {
              if (!error) {
                setError("CAPTCHA challenge failed. Please try again.");
              }
              setCaptchaToken(undefined);
            }}
            onExpire={() => {
              console.log("Turnstile token expired, resetting.");
              turnstileRef.current?.reset();
              setCaptchaToken(undefined);
            }}
          />

          {showVerification ? (
            <VerificationCodeInput
              email={email}
              onResend={handleResendVerification}
              loading={loading}
              resendDisabled={resendDisabled}
              resendTimer={resendTimer}
              submitButtonText="Verify Email"
              otpType="signup"
              captchaToken={captchaToken}
              onVerificationSuccess={handleSuccessfulLoginVerification}
              onVerificationError={handleFailedLoginVerification}
            />
          ) : showEmailForm ? (
            <form onSubmit={handleEmailLogin} className="space-y-6">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                isRequired
              />

              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                isRequired
              />

              <div className="text-right">
                <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 text-sm">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading || !captchaToken}
                loading={loading}
                className="w-full"
              >
                Login
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <OAuthButtonGroup
                onLoadingChange={setLoading}
                onError={setError}
                source="login_page"
                redirectUrlPath="/login"
              />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <Button
                variant="filled"
                className="w-full text-white"
                onClick={() => {
                  setShowEmailForm(true);
                  captureCustomEvent('login_started', { eventProperties: { method: 'email' } });
                }}
              >
                <svg className="w-5 h-5 mr-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Login via Email
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center p-6 text-gray-400 text-sm">
        <div className="mb-2">
          Copyright © {new Date().getFullYear()} holito |{' '}
          <Link to="/terms" className="hover:text-gray-300">Terms of Service</Link> |{' '}
                      <a href="https://holito.app/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">Privacy Policy</a>
        </div>
      </footer>
    </div>
  );
};